import { Listing_Categories } from '@oneappexchange/oaf-schema';
import { AxiosResponse } from 'axios';
import { BUCKETS, OAF_EXPLORE_URL } from '~/modules/config';
import { buildListingCategory, flattenBusinessNeeds } from '~/modules/helpers/categoryHelper';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { buildListingUrl } from '~/modules/helpers/searchServiceHelper';
import { categoryOptionsOrder } from '~/modules/helpers/searchSortOptionsHelper';
import { Routes } from '~/modules/routes';
import { BucketSection, SectionLabels } from '~/modules/stores/exploreStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { http } from '~/modules/utilities/http';
import { SearchContextListing, SearchResultListing } from '~/schemas/search-schema-v1';

export interface ExploreResultListing
    extends Omit<SearchResultListing, 'context' | 'external_id' | 'listingCategories'> {
    context?: SearchContextListing;
}

type ExploreListing = {
    external_id: string;
    technology: string;
    name: string;
    description: string;
    publisher_name: string;
    average_rating: number;
    review_count: number;
    price_model_type: string;
    chat_active: boolean;
    businessNeeds: Listing_Categories;
    plugins: schema.oaf.ListingPlugin[];
};
export type ExploreSection = {
    name: keyof SectionLabels;
    listings: ExploreListing[];
};

const buildSectionUrl = (bucket: string, sectionName: string) => {
    const basePath = Routes.EXPLORE.replace(':bucket', bucket);
    const encodedSectionName = encodeURIComponent(sectionName);
    switch (bucket) {
        case BUCKETS.EXPLORE_BUSINESS_NEEDS:
            return `/${basePath}?category=${encodedSectionName}`;
        case BUCKETS.EXPLORE_PRODUCTS:
            return `/${basePath}?product=${encodedSectionName}`;
        case BUCKETS.EXPLORE_INDUSTRIES:
            return `/${basePath}?industry=${encodedSectionName}`;
        case BUCKETS.EXPLORE_IMPACT:
            return `/${basePath}?impact=${encodedSectionName}`;
        case BUCKETS.EXPLORE_LABS:
            return `/${basePath}?tech=${encodedSectionName}`;
        default:
            return '';
    }
};

const getLogoSet = (oafListing: ExploreListing): schema.oaf.LogoSetItem[] => {
    const logoSet = oafListing.plugins?.find((plugin) => plugin.pluginType === 'listing/plugins/LogoSet')
        ?.data as schema.oaf.LogoSet;
    return logoSet?.items;
};

const buildCategories = (listing: ExploreListing) => {
    return flattenBusinessNeeds(listing.businessNeeds).map(buildListingCategory);
};

const parseOafResponse = (response: AxiosResponse, bucket: string, labels: SectionLabels): BucketSection[] => {
    if (!response.data || response.data.length === 0) {
        return [];
    }

    return response.data.sections.map((section: ExploreSection) => {
        return {
            name: section.name,
            label: labels[section.name],
            url: buildSectionUrl(bucket, section.name),
            listings: section.listings.map((listing) => {
                const logoSetItems = getLogoSet(listing);
                const logos = logoSetItems?.filter((logo) => logo.mediaId);
                return {
                    oafId: listing.external_id,
                    external_id: listing.external_id,
                    type: listing.technology,
                    title: listing.name,
                    description: listing.description,
                    listingCategories: buildCategories(listing),
                    logos,
                    publisher: listing.publisher_name,
                    averageRating: listing.average_rating || 0,
                    reviewsAmount: listing.review_count || 0,
                    pricing: listing.price_model_type,
                    chatEnabled: listing.chat_active || false,
                    sponsored: false,
                    listingUrl: buildListingUrl(listing.external_id),
                } as ExploreResultListing;
            }),
        };
    });
};
const sortBusinessNeeds = (a: BucketSection, b: BucketSection): number => {
    return categoryOptionsOrder.indexOf(a.name) - categoryOptionsOrder.indexOf(b.name);
};

const sortSectionNameAlphabetically = (a: BucketSection, b: BucketSection): number => {
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
};

export const getBucketSections = async (bucket: string, labels: SectionLabels): Promise<BucketSection[]> => {
    const language = getPreferredLanguage();
    const endpoint = `${OAF_EXPLORE_URL}/${bucket}?language=${language}`;
    try {
        const response = await http.get(endpoint);
        const parsedResponse = parseOafResponse(response, bucket, labels);
        if (parsedResponse?.length === 0) {
            return [];
        }

        if (bucket === BUCKETS.EXPLORE_BUSINESS_NEEDS) {
            return parsedResponse.sort(sortBusinessNeeds);
        }
        return parsedResponse.sort(sortSectionNameAlphabetically);
    } catch (error) {
        handleException(error, 'exploreService.getBucketSections');
        throw error;
    }
};
